import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Pencil, ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { Label } from '@ui/label.tsx';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@ui/dialog.tsx';

import {
  adminWorkspaceConfigs,
  apiShopsGetCollection,
  apiAdminworkspaceConfigsIdPatch,
  apiAdminworkspaceConfigsPost
} from '@r/api/generated/client.ts';

import type {
  ShopShopReadModel,
  ConfigConfigReadModel,
  WorkspaceConfigWorkspaceConfigReadModel
} from '@r/api/generated/client.schemas.ts';

import { useAllConfigs, getConfigsFromResponse } from '@r/hooks/use-all-configs.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

const SECRET_MASK = '*'.repeat(28);

interface EditTarget {
  configDef: ConfigConfigReadModel;
  workspaceConfig: WorkspaceConfigWorkspaceConfigReadModel | undefined;
}

interface ConfigRow {
  configDef: ConfigConfigReadModel;
  workspaceConfig: WorkspaceConfigWorkspaceConfigReadModel | undefined;
}

interface ConfigTableProps {
  rows: ConfigRow[];
  onEdit: (target: EditTarget) => void;
}

const ConfigTable = ({ rows, onEdit }: ConfigTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<ConfigRow>[]>(
    () => [
      {
        accessorKey: 'configDef.key',
        header: t('shops.settings.table.key'),
        cell: ({ row }) => row.original.configDef.key
      },
      {
        accessorKey: 'workspaceConfig.value',
        header: t('shops.settings.table.value'),
        cell: ({ row }) => {
          const { configDef, workspaceConfig } = row.original;
          if (!workspaceConfig?.value) return '-';
          return configDef.isSecret ? SECRET_MASK : workspaceConfig.value;
        }
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => onEdit(row.original)}
            className="float-end text-muted-foreground transition-colors hover:text-foreground"
          >
            <Pencil className="w-4 h-4" />
          </button>
        )
      }
    ],
    [t, onEdit]
  );

  const { table } = useDataTable({ data: rows, columns, pageCount: 1 });

  return <DataTable table={table} pagination={false} className="rounded-xl" />;
};

export const ShopsSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();

  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [newValue, setNewValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const allConfigsQuery = useAllConfigs();
  const shopConfigDefs = (getConfigsFromResponse(allConfigsQuery.data) ?? []).filter(
    (c) => c.type === 'shop'
  );

  const shopsQuery = useQuery<ShopShopReadModel[]>({
    queryKey: ['shops'],
    queryFn: async () => {
      const response = await apiShopsGetCollection();
      return (response.data ?? []) as ShopShopReadModel[];
    }
  });

  const workspaceConfigsQuery = useQuery<WorkspaceConfigWorkspaceConfigReadModel[]>({
    queryKey: ['workspaceConfigs', 'shop'],
    queryFn: async () => {
      const response = await adminWorkspaceConfigs({ 'config.type': 'shop' });

      return ((response.data ?? []) as WorkspaceConfigWorkspaceConfigReadModel[]).filter(
        (wc) => wc.config.type === 'shop'
      );
    }
  });

  const workspaceConfigs = workspaceConfigsQuery.data ?? [];

  const sections = (shopsQuery.data ?? [])
    .map((shop) => ({
      displayName: shop.displayName,
      rows: shopConfigDefs
        .filter((def) => def.key.startsWith(shop.name))
        .map((def) => ({
          configDef: def,
          workspaceConfig: workspaceConfigs.find((wc) => wc.config.key === def.key)
        }))
    }))
    .filter((section) => section.rows.length > 0);

  const openEdit = (target: EditTarget) => {
    setEditTarget(target);
    setNewValue('');
  };

  const closeEdit = () => {
    setEditTarget(null);
    setNewValue('');
  };

  const handleSave = async () => {
    if (!editTarget) return;
    setSubmitting(true);
    try {
      const { configDef, workspaceConfig } = editTarget;

      if (workspaceConfig?.id) {
        await apiAdminworkspaceConfigsIdPatch(String(workspaceConfig.id), { value: newValue });
      } else {
        await apiAdminworkspaceConfigsPost({
          value: newValue,
          config: `/configs/${configDef.id}`
        });
      }

      await queryClient.invalidateQueries({ queryKey: ['workspaceConfigs', 'shop'] });
      toast.success(t('admin.translations.edit.form.success'));
      closeEdit();
    } catch (error) {
      showOrvalErrorToast(error, t('toast.error.label'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col space-y-10 grow">
      <div className="space-y-3">
        <h1 className="text-4xl font-bold">{t('menu.admin.shops.settings')}</h1>
      </div>

      <div className="space-y-5">
        {sections.map((section) => (
          <Card key={section.displayName} className="w-full">
            <CardHeader>
              <CardTitle className="text-xl font-semibold">{section.displayName}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              <ConfigTable rows={section.rows} onEdit={openEdit} />
            </CardContent>
          </Card>
        ))}
      </div>

      <div>
        <Button variant="outline" onClick={() => navigate({ to: '/settings' })}>
          <ArrowLeft className="mr-2 w-4 h-4" />
          {t('common.back')}
        </Button>
      </div>

      <Dialog
        open={!!editTarget}
        onOpenChange={(open) => {
          if (!open) closeEdit();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('shops.settings.modal.title')}</DialogTitle>
            <DialogDescription>{t('shops.settings.modal.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('shops.settings.modal.current.value')}</Label>
              <Input
                value={
                  editTarget?.workspaceConfig?.value
                    ? editTarget.configDef.isSecret
                      ? SECRET_MASK
                      : editTarget.workspaceConfig.value
                    : ''
                }
                disabled
                readOnly
              />
            </div>
            <div className="space-y-2">
              <Label>{t('shops.settings.modal.new.value')}</Label>
              <Input
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder={editTarget?.configDef.key}
              />
            </div>
            <Button
              className="w-full"
              rounded="full"
              loading={submitting}
              disabled={!newValue.trim()}
              onClick={handleSave}
            >
              <Save className="mr-2 w-4 h-4" />
              {t('form.save.modifications')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
